import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// mui
import { ThemeProvider } from '@mui/material';
// common
import Router from './common/route/Router';
import RouterList from './common/route/RouterList';
import theme from '@/theme';
import { SFToastContainer } from '@/component/common/sf-toast-container';
import CssBaseline from '@mui/material/CssBaseline';

import { setStorage } from './lib/storage';
import { urlSessionStorage } from './lib/storage';
import { changeLang } from './lib/util';

import { Global, css } from '@emotion/react';
import fontsStyle from '@/assets/fonts/fonts';
import createEmotionCache from '@/createEmotionCache';
import { CacheProvider } from '@emotion/react';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga';
import { useCacheBuster } from 'react-cache-buster';
import packageJson from '../package.json'; // '../package.json';
import { appUpdate } from '@/lib/appUpdate/index';
import OutLink from './page/OutLink';
import Oauth from './page/Oauth';
import { SessionRefresh } from './lib/web3Auth/SessionRefresh';

import { webCheck } from './webVersion';

import { Web3AuthProvider } from '@web3auth/modal/react';
import web3AuthContextConfig from './contexts/web3authContext';
import { WagmiProvider } from '@web3auth/modal/react/wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Web3Provider } from './contexts/web3Provider';
import { readVersion } from '@/api/server/user/read';
import { compareVersions } from './lib/versionCheck';

const emotionCache = createEmotionCache();
const queryClient = new QueryClient();

export default function App() {
  const version = packageJson.version;

  const navigate = useNavigate();
  const { checkCacheStatus } = useCacheBuster();

    // 버전 체크 함수
    const checkVersion = async () => {
      try {
        // 이미 새로고침했는지 확인 (무한 루프 방지)
        const hasReloaded = sessionStorage.getItem('version-check-reloaded');
        if (hasReloaded === 'true') {
          console.log(`hasReloaded: ${hasReloaded}`);
          return;
        }

        const clientVersion = packageJson.version;
        const serverResponse = await readVersion();
        console.log(`serverResponse: ${serverResponse}`);
        const serverVersion = serverResponse?.data.version;
        console.log(`serverVersion: ${serverVersion}`);
        if (!serverVersion) {
          console.log('Server version not found');
          return;
        }
        // 서버 버전이 클라이언트 버전보다 높으면 새로고침
        if (serverVersion  && compareVersions(serverVersion, clientVersion) > 0) {
          sessionStorage.setItem('version-check-reloaded', 'true');
          window.location.reload();
        }
      } catch {
        // 버전 체크 실패는 무시
      }
    };

    // 초기 버전 체크
    useEffect(() => {
      if (process.env.NODE_ENV === 'production') {
        console.log(`in checkVersion`);

        checkVersion();
      }
    }, []);

  useEffect(() => {
    console.log(`version: ${version}`);
    checkCacheStatus();

    return () => {};
  }, [checkCacheStatus]);

  useEffect(() => {
    const isWeb = webCheck(packageJson.version);
    if (isWeb === false) {
      window.location.reload();
      navigate('/');
    }
    return () => {};
  }, []);

  // ----- 새로고침 막기 변수 -----
  // const preventClose = (e: BeforeUnloadEvent) => {
  //   e.preventDefault();
  //   e.returnValue = ''; // chrome에서는 설정이 필요해서 넣은 코드
  // };

  // // 브라우저에 렌더링 시 한 번만 실행하는 코드
  // useEffect(() => {
  //   (() => {
  //     window.addEventListener('beforeunload', preventClose);
  //   })();

  //   return () => {
  //     window.removeEventListener('beforeunload', preventClose);
  //   };
  // }, []);
  // // ----- 새로고침 막기 변수 end -----

  const userLang = navigator.language;
  const lang = changeLang(userLang);
  const langPayload = {
    key: 'lang',
    value: { lang },
  };
  setStorage(langPayload);

  const location = useLocation();
  const path = location.pathname;
  const type = RouterList().find((item: any) => item.url === path);
  if (path.startsWith('/outLink')) {
    console.log(`in outLink`);
    return <OutLink />;
  } else if (path.startsWith('/oauth')) {
    console.log(`in outLink`);
    return <Oauth />;
  } else if (type?.url) {
    const beForeUrl = urlSessionStorage().url;
    let url = beForeUrl !== '/signInSns' ? beForeUrl : type.url;
    const payload = {
      key: 'url',
      value: { url },
    };
    setStorage(payload);
  }
  if (process.env.NODE_ENV === 'production') {
    let device = null;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      // 모바일인 경우
      device = 'mobile';
    } else {
      device = 'other';
    }
    ReactGA.set({ hitType: device, page: path });
    ReactGA.pageview(path);
  }

  // if (projectId === undefined) {
  //   alert('project ID undefined');
  //   return <>error project ID</>;
  // }

  return (
    <div className="App">
      <SFToastContainer
        closeButton={false}
        hideProgressBar
        position="top-center"
        style={{
          paddingLeft: '24px',
          paddingRight: '24px',
          top: '50px',
        }}
        icon={
          <CheckCircleOutlineRoundedIcon
            sx={{
              fontSize: '16px',
            }}
          />
        }
      />
      <Web3AuthProvider config={web3AuthContextConfig}>
        <Web3Provider>
          <QueryClientProvider client={queryClient}>
            <WagmiProvider>
              <CacheProvider value={emotionCache}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <SessionRefresh>
                    <Router path={path} />
                  </SessionRefresh>
                </ThemeProvider>
                <Global
                  styles={css`
                    ${fontsStyle}
                  `}
                />
              </CacheProvider>
            </WagmiProvider>
          </QueryClientProvider>
        </Web3Provider>
      </Web3AuthProvider>
    </div>
  );
}
